/**
 * View Subscription Page
 *
 * Route: /subscription (subscriptionId passed via navigation state)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingOutlined, WarningOutlined, HomeOutlined } from '@ant-design/icons';
import { getSubscriptionV2, getSubscriptionTransactions, type SubscriptionV2Summary, type Transaction } from '../../api/payment';
import { getStudentById } from '../../api/students';
import { getCampusesByIds, getShiftsByIds } from '../../api/user';
import { ROUTES } from '@utils/constants';
import { PaymentSummaryCard } from '../../components/subscription/PaymentSummaryCard';
import { PaymentHistoryCard } from '../../components/subscription/PaymentHistoryCard';
import { PoliciesSection } from '../../components/subscription/PoliciesSection';
import { RouteInfoTile } from '../../components/subscription/RouteInfoTile';
import { RoutingDisclaimer } from '../../components/ui/RoutingDisclaimer/RoutingDisclaimer';
import {
  computePaymentSummary,
  formatGrade,
} from '../../utils/subscriptionHelpers';
import { formatDate } from '../../utils/dateFormatter';
import { calculateEstimatedPickupTime } from '../../utils/formatters';
import './styles/viewsubscription.css';
import { StudentStatus } from '@dev-zeromoblt/o-platform-client';

export function ViewSubscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionId = (location.state as any)?.subscriptionId as string | undefined;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionV2Summary | null>(null);

  const [studentName, setStudentName] = useState<string | undefined>();
  const [grade, setGrade] = useState<string | undefined>();
  const [campusName, setCampusName] = useState<string | undefined>();
  const [shiftLabel, setShiftLabel] = useState<string | undefined>();
  const [pickupLocation, setPickupLocation] = useState<string | undefined>();
  const [studentLocation, setStudentLocation] = useState<{ lat: number; lng: number } | undefined>();
  const [travelTime, setTravelTime] = useState<number | undefined>();
  const [shiftStartTime, setShiftStartTime] = useState<string | undefined>();
  const [validFrom, setValidFrom] = useState<string | undefined>();
  const [validUntil, setValidUntil] = useState<string | undefined>();
  const [kmSlab, setKmSlab] = useState<string | undefined>();
  const [studentStatus, setStudentStatus] = useState<string | undefined>();
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  useEffect(() => {
    if (!subscriptionId) { navigate(ROUTES.WELCOME); return; }

    const run = async () => {
      try {
        setLoading(true);
        const sub = await getSubscriptionV2(subscriptionId);
        if (!sub?.id) throw new Error('Subscription not found.');
        setSubscription(sub as unknown as SubscriptionV2Summary);

        const granteeId = (sub as any).granteeId;
        if (granteeId) {
          const studentData = await getStudentById(granteeId);
          if (studentData) {
            setStudentName(studentData.name ?? undefined);
            setGrade((studentData as any).grade ?? undefined);
            setPickupLocation((studentData as any).geoAddress || undefined);
            setStudentStatus((studentData as any).status ?? undefined);

            // Store location for maps link
            if ((studentData as any).location) {
              setStudentLocation((studentData as any).location);
            }

            // Store travel time from tags
            const tags = (studentData as any).tags;
            if (tags?.travelTime) {
              setTravelTime(Math.round(parseFloat(String(tags.travelTime))));
            }

            // Use student's startDate as validFrom
            if (studentData.startDate) {
              setValidFrom(
                typeof studentData.startDate === 'string'
                  ? studentData.startDate
                  : new Date(studentData.startDate as number).toISOString().split('T')[0],
              );
            }

            const [campusRes, shiftRes] = await Promise.allSettled([
              studentData.campusId ? getCampusesByIds([studentData.campusId]) : Promise.resolve(null),
              studentData.shifts?.[0] ? getShiftsByIds([studentData.shifts[0]]) : Promise.resolve(null),
            ]);
            if (campusRes.status === 'fulfilled' && campusRes.value?.campuses?.[0]) {
              setCampusName(campusRes.value.campuses[0].name ?? undefined);
            }
            if (shiftRes.status === 'fulfilled' && shiftRes.value?.shifts?.[0]) {
              const shift = shiftRes.value.shifts[0];
              setShiftLabel(shift.displayLabel || shift.name || undefined);
              // Store shift start time for pickup calculation
              if ((shift as any).schedule?.startsAt) {
                setShiftStartTime((shift as any).schedule.startsAt);
              }
              // Only use shift's validUntil (end date)
              if (shift.validUntil) {
                setValidUntil(
                  typeof shift.validUntil === 'string'
                    ? shift.validUntil
                    : new Date(shift.validUntil as number).toISOString().split('T')[0],
                );
              }
            }
          }
        }

        const tier = (sub as any).pricingDetails?.tier;
        if (tier?.rangeKm) setKmSlab(`${tier.rangeKm.from}–${tier.rangeKm.to} km`);

        const txns = await getSubscriptionTransactions(subscriptionId);
        setTransactions(txns);
      } catch (err: any) {
        setError(err.message || 'Failed to load subscription details.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [subscriptionId, navigate]);

  if (loading) {
    return (
      <div className="view-sub__loading">
        <LoadingOutlined style={{ fontSize: 32, color: '#22c55e' }} />
        <p>Loading subscription details...</p>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="view-sub__error">
        <WarningOutlined style={{ fontSize: 40, color: '#f59e0b' }} />
        <p>{error ?? 'Subscription not found.'}</p>
        <button onClick={() => navigate(ROUTES.WELCOME)} className="view-sub__back-btn">
          <HomeOutlined /> Go to Dashboard
        </button>
      </div>
    );
  }

  const sub = subscription;
  const token = sub.token;
  const allInstallments = sub.paymentCalendar?.installments ?? [];

  const summary = computePaymentSummary(
    token ?? null,
    sub.paymentCalendar?.totalAmount ?? 0,
    allInstallments,
    transactions,
  );

  const shiftValidFrom = validFrom || sub.shiftDetails?.validFrom;
  const shiftValidUntil = validUntil || sub.shiftDetails?.validUntil;

  const displayKmSlab = kmSlab || (() => {
    const tier = sub.pricingDetails?.tier;
    return tier?.rangeKm ? `${tier.rangeKm.from}–${tier.rangeKm.to} km` : undefined;
  })();

  const policies = sub.paymentPlan?.policies ?? null;

  const pendingCount = allInstallments.filter(
    (i: any) => i.status !== 'PAID' && !i.paidAt,
  ).length;

  const statusKey = sub.status?.toLowerCase().replace(/ /g, '_') ?? '';

  // Estimated pickup time from shift start and travel time
  const estimatedPickupTime = calculateEstimatedPickupTime(shiftStartTime, travelTime);

  // Pickup maps URL
  const pickupMapsUrl = studentLocation
    ? `https://www.google.com/maps?q=${studentLocation.lat},${studentLocation.lng}`
    : undefined;

  return (
    <div className="view-sub__page">
      {/* ── Dark hero: header + student info ── */}
      <div className="view-sub__hero">
        <div className="view-sub__header">
          <button className="view-sub__back-link" onClick={() => navigate(ROUTES.WELCOME)}>
            ‹ Back
          </button>
          <span className="view-sub__header-title">Subscription Details</span>
        </div>

        <div className="view-sub__student-header">
          {studentName && <h2 className="view-sub__student-name">{studentName}</h2>}
          {(grade || campusName) && (
            <p className="view-sub__student-meta">
              {grade ? formatGrade(grade) : ''}
              {grade && campusName ? ' · ' : ''}
              {campusName ?? ''}
            </p>
          )}
          <div className="view-sub__status-row">
            {sub.status && (
              <span className={`view-sub__status-badge view-sub__status-badge--${statusKey}`}>
                <span className="view-sub__status-dot" />
                {sub.status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="view-sub__content">

        {/* Payment Summary */}
        <PaymentSummaryCard
          totalPaid={summary.totalPaid}
          totalSubscription={summary.totalSubscription}
          adjustedPaid={summary.adjustedPaid}
          adjustedTotal={summary.adjustedTotal}
          remaining={summary.remaining}
          progressPct={summary.progressPct}
          pendingInstallments={pendingCount}
        />

        {/* Route Info */}
        {sub.granteeId && (sub.status === 'ACTIVE' && studentStatus === StudentStatus.ACTIVE) && (
          <div className="view-sub__section">
            <h3 className="view-sub__section-title">Your Route</h3>
            <RouteInfoTile studentId={sub.granteeId} variant="card" />
          </div>
        )}

        {/* Pending Routing Disclaimer */}
        {sub.granteeId &&  (sub.status === 'ACTIVE' && studentStatus === StudentStatus.PENDING_ROUTING) && (
          <div className="view-sub__section">
            <h3 className="view-sub__section-title">Your Route</h3>
            <RoutingDisclaimer variant="full" />
          </div>
        )}

        {/* Route & Plan */}
        <div className="view-sub__section">
          <h3 className="view-sub__section-title">Subscription Details</h3>
          <div className="view-sub__card">
            {shiftLabel && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Shift</span>
                <span className="view-sub__kv-value">{shiftLabel}</span>
              </div>
            )}
            {pickupLocation && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Pickup Location</span>
                <span className="view-sub__kv-value">
                  {pickupMapsUrl ? (
                    <a
                      href={pickupMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#3b82f6' }}
                    >
                      📍 View on Maps
                    </a>
                  ) : pickupLocation}
                </span>
              </div>
            )}
            {campusName && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Drop Location</span>
                <span className="view-sub__kv-value">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(campusName)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#3b82f6' }}
                  >
                    {campusName}
                  </a>
                </span>
              </div>
            )}
            {displayKmSlab && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Distance Slab</span>
                <span className="view-sub__kv-value">{displayKmSlab}</span>
              </div>
            )}
            {shiftValidFrom && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Subscription Start</span>
                <span className="view-sub__kv-value">{formatDate(shiftValidFrom)}</span>
              </div>
            )}
            {shiftValidUntil && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Subscription End</span>
                <span className="view-sub__kv-value">{formatDate(shiftValidUntil)}</span>
              </div>
            )}
            {estimatedPickupTime && (
              <div className="view-sub__kv-row">
                <span className="view-sub__kv-label">Est. Pickup Time</span>
                <span className="view-sub__kv-value">{estimatedPickupTime}</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment History */}
        <PaymentHistoryCard
          token={token && (token.amount ?? 0) > 0 ? token : undefined}
          installments={allInstallments}
          transactions={transactions}
        />

        {/* Policies */}
        <PoliciesSection policies={policies} />
      </div>
    </div>
  );
}

export default ViewSubscription;
